import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { parse, ParsedQuery, stringify } from 'query-string'
import { postcodeIsValid } from '@at/formatter-utils/lib/postcode-utils/postcode-validity.util'
import { useHistory } from 'react-router-dom'
import { History } from 'history'
import { setPostcodeCookie } from '../utils/cookie'
import { FilterQueryParam } from '../utils/search/filter-query-param-util/query-params'
import { purgeCache } from '../graphql/cache'
import { getStoredPostcode } from '../utils/search/local-search.util'
import { useIsLeasingSearch } from '../utils/is-leasing-search'

type Postcode = string | undefined
type LatLong = string | undefined
type Location = string | undefined

interface LocationQueryParams {
    [key: string]: Postcode | LatLong | Location
}

type UpdateStateFunction<T> = (value: T) => void

interface UserLocation {
    postcode: Postcode
    'lat-long': LatLong
    location: Location
}

export enum Error {
    NONE,
    CLIENT,
    EXTERNAL,
}

export enum DeviceLocationStatus {
    NOT_USED,
    REQUESTED,
    UNAVAILABLE,
    SUCCESS,
}

export type UserLocationContextType = {
    postcode: Postcode
    latLong: LatLong
    location: Location
    deviceLocationStatus: DeviceLocationStatus
    error: Error
    updatePostcode: UpdateStateFunction<Postcode>
    updateLatLong: UpdateStateFunction<LatLong>
    updateLocation: UpdateStateFunction<LatLong>
    setError: UpdateStateFunction<Error.NONE | Error.EXTERNAL>
    requestDeviceLocation: () => void
}

const defaultContextValue: UserLocationContextType = {
    postcode: undefined,
    latLong: undefined,
    location: undefined,
    deviceLocationStatus: DeviceLocationStatus.NOT_USED,
    error: Error.NONE,
    updatePostcode: () => undefined,
    updateLatLong: () => undefined,
    updateLocation: () => undefined,
    setError: () => Error.NONE,
    requestDeviceLocation: () => undefined,
}

export const UserLocationContext = React.createContext<UserLocationContextType>(defaultContextValue)

const hasValidUserLocation = (postcode: Postcode, latLong: LatLong, location: Location, isLeasingSearch: boolean) =>
    isLeasingSearch || location || latLong !== undefined || (postcode !== undefined && postcodeIsValid(postcode))

const firstNonNull: <T>(value: T | null | Array<T | null>) => T | null = (value) => {
    if (Array.isArray(value)) {
        return value.filter((val) => val !== null)[0]
    } else {
        return value
    }
}

const useRequestDeviceLocation = (
    deviceLocationStatus: DeviceLocationStatus,
    setDeviceLocationStatus: UpdateStateFunction<DeviceLocationStatus>,
    setLatLong: UpdateStateFunction<LatLong>
) =>
    useCallback(() => {
        if (deviceLocationStatus === DeviceLocationStatus.REQUESTED) {
            // Already requested... skip
            return
        }
        const onSuccess = (pos: { coords: { latitude: number; longitude: number } }) => {
            setDeviceLocationStatus(DeviceLocationStatus.SUCCESS)
            setLatLong(`${pos.coords.latitude}, ${pos.coords.longitude}`)
        }
        const onError = () => {
            setDeviceLocationStatus(DeviceLocationStatus.UNAVAILABLE)
        }
        window.navigator.geolocation.getCurrentPosition(onSuccess, onError)
    }, [deviceLocationStatus, setDeviceLocationStatus, setLatLong])

const useSyncStateWithCookies = (postcode: Postcode) => {
    useEffect(() => {
        if (postcode && postcodeIsValid(postcode)) {
            setPostcodeCookie(`postcode=${postcode}`)
        }
    }, [postcode])
}

const userLocationFrom = (key: string, value: Postcode | LatLong | Location): UserLocation => {
    const emptyUserLocation: UserLocation = {
        postcode: undefined,
        'lat-long': undefined,
        location: undefined,
    }
    return {
        ...emptyUserLocation,
        [key]: value,
    }
}

const getParamValue = (queryParams: ParsedQuery, name: FilterQueryParam) => firstNonNull(queryParams[name]) ?? undefined

const getInitialUserLocation = (queryParams: ParsedQuery): UserLocation => {
    const locationParam = getParamValue(queryParams, FilterQueryParam.location)
    if (locationParam === 'global') {
        return userLocationFrom('location', locationParam)
    }
    const latLongParam = getParamValue(queryParams, FilterQueryParam.lat_long)
    if (latLongParam?.length) {
        return userLocationFrom('lat-long', latLongParam)
    }
    return userLocationFrom('postcode', getParamValue(queryParams, FilterQueryParam.postcode) ?? getStoredPostcode())
}

const useAllLocationState = (
    queryParams: ParsedQuery
): [UserLocation, UpdateStateFunction<Location>, UpdateStateFunction<Postcode>, UpdateStateFunction<LatLong>] => {
    const [userLocation, setUserLocation] = useState<UserLocation>(getInitialUserLocation(queryParams))
    const setPostcode = (value: Postcode) => setUserLocation(userLocationFrom('postcode', value))
    const setLatLong = (value: LatLong) => setUserLocation(userLocationFrom('lat-long', value))
    const setLocation = (value: Location) => setUserLocation(userLocationFrom('location', value))
    return [userLocation, setPostcode, setLatLong, setLocation]
}

const updateUrl = (nextQueryParams: ParsedQuery | LocationQueryParams, history: History) => {
    const { push } = history
    const { pathname, hash } = history.location
    if (nextQueryParams[FilterQueryParam.location] === 'global') {
        nextQueryParams[FilterQueryParam.radius] = undefined
    }
    nextQueryParams[FilterQueryParam.page] = undefined // Reset search journey pagination
    purgeCache()
    push(`${pathname}?${stringify(nextQueryParams)}${hash ? hash : ''}`)
}

const useUpdateUrlWithUserLocation = (
    userLocation: UserLocation,
    queryParams: ParsedQuery,
    history: History,
    isLeasingSearch: boolean
) => {
    useEffect(() => {
        const hasUserLocationChanged =
            queryParams[FilterQueryParam.postcode] !== userLocation.postcode ||
            queryParams[FilterQueryParam.lat_long] !== userLocation['lat-long'] ||
            queryParams[FilterQueryParam.location] !== userLocation.location

        if (hasUserLocationChanged && !isLeasingSearch) {
            updateUrl({ ...queryParams, ...userLocation }, history)
        }
    }, [userLocation, queryParams, history, isLeasingSearch])
}

export const UserLocationContextProvider: React.FC<React.PropsWithChildren<object>> = ({ children }) => {
    const history = useHistory()
    const search = history.location.search
    const queryParams = useMemo(() => parse(search), [search])
    const [userLocation, setPostcode, setLatLong, setLocation] = useAllLocationState(queryParams)
    const { postcode, 'lat-long': latLong, location } = userLocation
    const [deviceLocationStatus, setDeviceLocationStatus] = useState(defaultContextValue.deviceLocationStatus)
    const [_error, setError] = useState<Error.NONE | Error.EXTERNAL>(Error.NONE)
    const isLeasingSearch = useIsLeasingSearch()
    const error = _error
        ? _error
        : !hasValidUserLocation(postcode, latLong, location, isLeasingSearch)
        ? Error.CLIENT
        : Error.NONE
    const requestDeviceLocation = useRequestDeviceLocation(deviceLocationStatus, setDeviceLocationStatus, setLatLong)
    useUpdateUrlWithUserLocation(userLocation, queryParams, history, isLeasingSearch)
    useSyncStateWithCookies(postcode)

    return (
        <UserLocationContext.Provider
            value={{
                postcode,
                latLong,
                location,
                deviceLocationStatus,
                error,
                setError,
                updatePostcode: setPostcode,
                updateLatLong: setLatLong,
                updateLocation: setLocation,
                requestDeviceLocation,
            }}
        >
            {children}
        </UserLocationContext.Provider>
    )
}
